import { QueuedAction } from "../ActionQueueStore";
import type { CheckoutData } from "../../model/CheckoutData";
import { buildUserData, updateHeaderRequest } from "../../model/CheckoutRequests";
import {
	submitCheckout,
	updateCheckoutHeader,
	removeItems,
	putVoucher,
	deleteVoucher,
} from "../../server/ServerRequestsInternal";
import { CheckoutWrapperActions } from "../../actions/CheckoutWrapperActions";
import { CheckoutAggregatorActions } from "../../actions/CheckoutAggregatorActions";
import { CheckoutSteps } from "../../model/CheckoutSteps";
import { OrderConfirmationPageActions } from "../../actions/OrderConfirmationPageActions";
import { ErrorHandler } from "../ErrorHandler";
import history from "../../uidispatcherapi/BettyHistoryModule";
import type { CheckoutAggregatorStoreImpl } from "./CheckoutAggregatorStore";
import type {
	RemoveItemsRequest,
	SubmitEvent,
	SubmitCheckoutRequest,
	UpdateHeaderEvent,
	VoucherRequest,
} from "../../model/CheckoutRequests";
import type { ErrorCodeHandler, ErrorHandlingStage } from "../ErrorHandler";
import { ServiceError } from "../BaseTypes";
import { isFsdOnlyCheckout } from "../../utils/CheckoutUtil";
import { trackOrderConfirmationData } from "../../utils/TrackingUtil";
import { TrackingActions } from "../../actions/TrackingActions";
import { intlRoot } from "../../IntlRootMixin";
const CHECKOUT_SUBMIT_ERROR = "CHECKOUT_SUBMIT_ERROR";
export type CheckoutAggregatorEvent = QueuedAction<CheckoutData>;
const checkoutErrorHandlers = [
	{
		canHandle: (error) => error.code === "OC-203" || error.code === "OC-202",
		handle: (error, notifications) => {
			CheckoutWrapperActions.abortCheckout();
			notifications.send({
				type: "server-error",
				key: "ORC_CHECKOUT_OUTDATED",
				id: "OC-203",
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-213",
		handle: (error, notifications) => {
			CheckoutWrapperActions.abortCheckout();
			notifications.send({
				type: "server-error",
				key: "ORC_ERROR_CHECKOUT_NEEDS_PRICES",
				id: "OC-213",
			});
		},
	},
];
export function getSubmitErrorHandlersWithRedirect(paymentsPageUrl: string): Array<ErrorCodeHandler> {
	return [
		{
			canHandle: (error) => error.code === "OC-243",
			handle: (error, notifications) => {
				notifications.send({
					type: "user-error",
					text: intlRoot.formatMessage({ id: "ORC_ERROR_EXCEEDED_MAX_AMOUNT_FOR_SINGLE_TRANSACTION" }),
					id: "OC-243",
				});
				history.push(paymentsPageUrl);
			},
		},
		{
			canHandle: (error) => error.code === "OC-244",
			handle: (error, notifications) => {
				notifications.send({
					type: "user-error",
					text: intlRoot.formatMessage({ id: "ORC_ERROR_INSUFFICIENT_FUNDS" }),
					id: "OC-244",
				});
				history.push(paymentsPageUrl);
			},
		},
		{
			canHandle: (error) => error.code === "OC-246",
			handle: (error, notifications) => {
				notifications.send({
					type: "server-error",
					text: intlRoot.formatMessage({ id: "ORC_ERROR_SUBMIT_FAILED_PAYMENT" }),
					id: "OC-246",
				});
				history.push(paymentsPageUrl);
			},
		},
		{
			canHandle: (error) => error.code === "OC-249",
			handle: (error, notifications) => {
				notifications.send({
					type: "server-error",
					text: intlRoot.formatMessage({ id: "ORC_ERROR_INVALID_CREDIT_CARD" }),
					id: "OC-249",
				});
				history.push(paymentsPageUrl);
			},
		},
	];
}
export class CheckoutErrorHandler extends ErrorHandler {
	constructor(errorCodeHandlers: Array<ErrorCodeHandler>) {
		super(errorCodeHandlers.concat(checkoutErrorHandlers));
	}
}
export class SubmitCheckout extends CheckoutErrorHandler implements CheckoutAggregatorEvent {
	//TODO TS apply: (arg0: CheckoutData) => void;
	isLazy = false;
	store: CheckoutAggregatorStoreImpl;
	submitEvent: SubmitEvent;
	locale: string;

	constructor(
		store: CheckoutAggregatorStoreImpl,
		submitEvent: SubmitEvent,
		locale: string,
		...errorCodeHandlers: Array<ErrorCodeHandler>
	) {
		super(errorCodeHandlers);
		this.store = store;
		this.submitEvent = submitEvent;
		this.locale = locale;
	}

	apply() {}

	send(
		checkoutData: CheckoutData | null | undefined,
		successCallback: (arg0: CheckoutData | null | undefined) => void,
		errorCallback: ErrorHandlingStage,
	): void {
		if (!checkoutData) {
			super.onError(
				null,
				new ServiceError({
					code: "OC-294",
					message: "Validation error before submitting checkout.",
				}),
			);
			return;
		}

		const userData = buildUserData(checkoutData);

		if (!userData) {
			super.onError(
				null,
				new ServiceError({
					code: "OC-294",
					message: "Could not build user data before submitting checkout.",
				}),
			);
			return;
		}

		this._specialErrorCodeHandlers = [
			...(this._specialErrorCodeHandlers || []),
			...getSubmitErrorHandlersWithRedirect(`/checkout${CheckoutSteps.Payment.url}`),
		];
		const params: SubmitCheckoutRequest = {
			userData,
			checkoutId: checkoutData.id,
			submitEvent: this.submitEvent,
			locale: this.locale,
		};
		submitCheckout(params, this.onSuccess.bind(this, successCallback), this.onError.bind(this, errorCallback));
	}

	onError(next: ErrorHandlingStage | null | undefined, error: ServiceError) {
		super.onError(next, error);
		// Reload checkout data after submit error.
		CheckoutAggregatorActions.onLoadCheckout(() => {});
	}

	onSuccess(
		successCallback: (arg0: CheckoutData | null | undefined) => void,
		checkoutData: CheckoutData | null | undefined,
	) {
		if (!checkoutData) {
			console.error("Submit checkout error: missing checkout");
			return;
		}

		this.store._state.checkoutStatus = checkoutData.status;
		CheckoutWrapperActions.removeMessage(CHECKOUT_SUBMIT_ERROR);
		OrderConfirmationPageActions.onCheckoutSubmitSuccess(checkoutData);

		if (!isFsdOnlyCheckout(checkoutData)) {
			trackOrderConfirmationData(checkoutData);
		}

		// Track synchronously to get the current location, before redirect.
		TrackingActions.trackSubmitSuccess.trigger(checkoutData);
		successCallback(checkoutData);
	}

	combine(other: CheckoutAggregatorEvent) {
		if (other instanceof SubmitCheckout) {
			return this;
		}

		return null;
	}
}
export class UpdateHeader<EventType extends UpdateHeaderEvent>
	extends CheckoutErrorHandler
	implements CheckoutAggregatorEvent
{
	event: EventType;
	isLazy: boolean;

	constructor(
		event: EventType,
		apply: (arg0: CheckoutData) => void,
		isLazy: boolean,
		...errorCodeHandlers: Array<ErrorCodeHandler>
	) {
		super(errorCodeHandlers);
		this.event = event;
		this.apply = apply;
		this.isLazy = isLazy;
	}
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	apply(checkoutData: CheckoutData) {}
	send(
		checkoutData: CheckoutData | null | undefined,
		successCallback: (arg0: CheckoutData | null | undefined) => void,
		errorCallback: ErrorHandlingStage,
	): void {
		const request = updateHeaderRequest(checkoutData, this.event);

		if (!request || !checkoutData) {
			console.error("cannot update header - no checkout or request");
			return;
		}

		updateCheckoutHeader(request, successCallback, super.onError.bind(this, errorCallback));
	}

	combine(other: CheckoutAggregatorEvent): CheckoutAggregatorEvent | null | undefined {
		if (other instanceof UpdateHeader && other.event.eventType === this.event.eventType) {
			// overwrite previous updates
			return this;
		}

		return null;
	}
}
export class RemoveItems extends CheckoutErrorHandler implements CheckoutAggregatorEvent {
	itemIds: string[];
	//apply: (arg0: CheckoutData) => void;
	isLazy = false;

	constructor(itemIds: string[], ...errorCodeHandlers: Array<ErrorCodeHandler>) {
		super(errorCodeHandlers);
		this.itemIds = itemIds;
	}

	apply() {}

	send(
		checkoutData: CheckoutData | null | undefined,
		successCallback: (arg0: CheckoutData | null | undefined) => void,
		errorCallback: ErrorHandlingStage,
	): void {
		const userData = buildUserData(checkoutData);

		if (!checkoutData || !userData) {
			console.error("RemoveItems: no checkout or user data");
			return;
		}

		const request: RemoveItemsRequest = {
			userData: userData,
			checkoutId: checkoutData.id,
			items: this.itemIds,
		};
		removeItems(request, successCallback, super.onError.bind(this, errorCallback));
	}

	combine(other: CheckoutAggregatorEvent): CheckoutAggregatorEvent | null | undefined {
		if (other instanceof RemoveItems) {
			return new RemoveItems([...new Set([...this.itemIds, ...other.itemIds])]);
		}

		return null;
	}
}
export class ForceUpdates implements CheckoutAggregatorEvent {
	//apply: (arg0: CheckoutData) => void;
	isLazy = false;

	apply() {}

	send(
		checkoutData: CheckoutData | null | undefined,
		successCallback: (arg0: CheckoutData | null | undefined) => void,
	): void {
		successCallback(checkoutData);
	}

	combine(): CheckoutAggregatorEvent | null | undefined {
		return null;
	}
}
export class ApplyVoucher extends CheckoutErrorHandler implements CheckoutAggregatorEvent {
	eventType = "ApplyVoucherEvent";
	code: string;
	// apply: (arg0: CheckoutData) => void;
	isLazy = false;
	successCallback: (code: string) => void;

	constructor(code: string, successCallback: (code: string) => void, ...errorCodeHandlers: Array<ErrorCodeHandler>) {
		super(errorCodeHandlers);
		this.code = code;
		this.successCallback = successCallback;
	}

	apply() {}

	send(
		checkoutData: CheckoutData | null | undefined,
		successCallback: (arg0: CheckoutData | null | undefined) => void,
		errorCallback: ErrorHandlingStage,
	): void {
		const userData = buildUserData(checkoutData);

		if (!checkoutData || !userData) {
			console.error("ApplyVoucher: no checkout or user data");
			return;
		}

		const request: VoucherRequest = {
			userData: userData,
			checkoutId: checkoutData.id,
			code: this.code,
		};
		putVoucher(request, this.onSuccess.bind(this, successCallback), this.onError.bind(this, errorCallback));
	}

	onSuccess(
		successCallback: (arg0: CheckoutData | null | undefined) => void,
		checkouts: CheckoutData | null | undefined,
	) {
		this.successCallback(this.code);
		successCallback(checkouts);
	}

	combine(other: CheckoutAggregatorEvent): CheckoutAggregatorEvent | null | undefined {
		if (other instanceof ApplyVoucher) {
			return this;
		}

		return null;
	}
}
export class RemoveVoucher extends CheckoutErrorHandler implements CheckoutAggregatorEvent {
	eventType = "RemoveVoucherEvent";
	code: string;
	//apply: (arg0: CheckoutData) => void;
	isLazy = false;

	constructor(code: string, ...errorCodeHandlers: Array<ErrorCodeHandler>) {
		super(errorCodeHandlers);
		this.code = code;
	}

	apply(checkoutData: CheckoutData) {
		const vouchers = checkoutData && checkoutData.checkoutPrices && checkoutData.checkoutPrices.vouchers;

		if (this.code && vouchers) {
			checkoutData.checkoutPrices.vouchers = vouchers.filter((voucher) => voucher.coupon !== this.code);
		}
	}

	send(
		checkoutData: CheckoutData | null | undefined,
		successCallback: (arg0: CheckoutData | null | undefined) => void,
		errorCallback: ErrorHandlingStage,
	): void {
		const userData = buildUserData(checkoutData);

		if (!checkoutData || !userData) {
			console.error("RemoveVoucher: no checkout or user data");
			return;
		}

		const request: VoucherRequest = {
			userData: userData,
			checkoutId: checkoutData.id,
			code: this.code,
		};
		deleteVoucher(request, successCallback, super.onError.bind(this, errorCallback));
	}

	combine(other: CheckoutAggregatorEvent): CheckoutAggregatorEvent | null | undefined {
		if (other instanceof RemoveVoucher) {
			return this;
		}

		return null;
	}
}
